import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { trackAction } from '@/idv2/utils/logger';
import type { CaptureKind } from '../../types';
import type { AutoCaptureKind, CaptureStatus } from '../camera/types';
import { readPrefersManualMode } from '../camera/utils/manual-mode';
type CaptureTelemetryProps = {
  autocapture: boolean;
  autocaptureKind: AutoCaptureKind;
  /** Only set for identity documents; autocaptureKind already distinguishes selfie and non-id-doc. */
  side?: 'front' | 'back';
  status?: CaptureStatus;
};

// idv sends the same events from its OpenCV and face-api detectors without an engine, so the
// document ramp can compare auto-capture success between the two engines per tenant.
const engine = 'vision';
const useCaptureTelemetry = t0 => {
  const $ = _c(17);
  const {
    autocapture,
    autocaptureKind,
    side,
    status
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Date.now();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openedAt = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const statusesSeen = useRef(t2);
  const reachedOk = useRef(false);
  let t3;
  let t4;
  if ($[2] !== autocaptureKind || $[3] !== side || $[4] !== status) {
    t3 = () => {
      if (!status || statusesSeen.current.has(status)) {
        return;
      }
      statusesSeen.current.add(status);
      if (status === "ok") {
        reachedOk.current = true;
      }
      trackAction(`document-capture:status-${status}`, {
        engine,
        autocaptureKind,
        side
      });
    };
    t4 = [status, autocaptureKind, side];
    $[2] = autocaptureKind;
    $[3] = side;
    $[4] = status;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== autocapture || $[8] !== autocaptureKind || $[9] !== side) {
    t5 = captureKind => {
      trackAction("document-capture:captured", {
        engine,
        autocaptureKind,
        side,
        captureKind,
        autoCaptureSucceeded: String(reachedOk.current),
        manualMode: String(!autocapture || readPrefersManualMode()),
        statusesSeen: Array.from(statusesSeen.current).join(","),
        msSinceOpened: Date.now() - openedAt.current
      });
    };
    $[7] = autocapture;
    $[8] = autocaptureKind;
    $[9] = side;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const reportCapture = t5;
  let t6;
  if ($[11] !== autocaptureKind || $[12] !== side) {
    t6 = () => {
      trackAction("document-capture:camera-stuck", {
        engine,
        autocaptureKind,
        side,
        statusesSeen: Array.from(statusesSeen.current).join(","),
        msSinceOpened: Date.now() - openedAt.current
      });
    };
    $[11] = autocaptureKind;
    $[12] = side;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const reportCameraStuck = t6;
  let t7;
  if ($[14] !== reportCameraStuck || $[15] !== reportCapture) {
    t7 = {
      reportCapture,
      reportCameraStuck
    };
    $[14] = reportCameraStuck;
    $[15] = reportCapture;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
export default useCaptureTelemetry;